import {useState, useMemo, useEffect, useRef, useCallback} from 'react';
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {Button} from '@/components/ui/button';
import {useToast} from '@/hooks/use-toast';
import {Plus} from 'lucide-react';
import {ConfirmDialog} from '@/components/ui/confirm-dialog';
import {
    createReward,
    deleteReward,
    getDomains,
    getRetranslateStatus,
    getRewards,
    retranslateReward,
    updateReward
} from '@/services/api';
import {Reward, RewardCreate, RewardRetranslateProgress} from '@/types/rewards';
import {RewardFormData, Language} from './types';
import {
    RewardCard,
    RewardCodeBuilderModal,
    RewardFormModal,
    RewardInstallModal,
    EmptyState,
    InfoBanner
} from './components';

// Polling do progresso da retradução. Não gasta tokens (só lê o Redis), mas
// tem teto mesmo assim: se a API sumir no meio, o painel para de perguntar.
const RETRANSLATE_POLL_MS = 2000;
const RETRANSLATE_POLL_MAX_MS = 10 * 60 * 1000;

export default function Rewards() {
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isCodeBuilderOpen, setIsCodeBuilderOpen] = useState(false);
    const [isInstallModalOpen, setIsInstallModalOpen] = useState(false);
    const [selectedReward, setSelectedReward] = useState<Reward | null>(null);
    const [activeTab, setActiveTab] = useState<'all' | 'standard' | 'quiz'>('all');
    // Retraduzir gasta tokens da OpenAI: passa por duas confirmações antes de chamar a API.
    const [retranslateTarget, setRetranslateTarget] = useState<Reward | null>(null);
    const [retranslateStep, setRetranslateStep] = useState<1 | 2>(1);
    // Só uma retradução por vez no painel; o progresso fica no card até o usuário sair da tela.
    const [retranslatingId, setRetranslatingId] = useState<number | null>(null);
    const [retranslateProgress, setRetranslateProgress] = useState<Record<number, RewardRetranslateProgress>>({});
    const pollStartedAt = useRef<number>(0);

    const {toast} = useToast();
    const queryClient = useQueryClient();

    const {data: domains = []} = useQuery({
        queryKey: ['domains'],
        queryFn: getDomains,
    });

    const {data: rewards, isLoading} = useQuery({
        queryKey: ['rewards'],
        queryFn: getRewards,
    });

    const {data: availableLanguages = []} = useQuery<Language[]>({
        queryKey: ['languages'],
        queryFn: async () => {
            const response = await fetch('https://api.rocketgroup.io/api/v1/public/rewards/languages');
            return response.json();
        },
    });

    const createMutation = useMutation({
        mutationFn: createReward,
        onSuccess: () => {
            toast({
                title: 'Offerwall criado!',
                description: 'O reward foi criado com sucesso.',
            });
            queryClient.invalidateQueries({queryKey: ['rewards']});
            setIsCreateDialogOpen(false);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao criar reward',
                description: error.message || 'Ocorreu um erro ao criar o reward.',
            });
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({id, data}: { id: number; data: Partial<RewardCreate> }) =>
            updateReward(id, data),
        onSuccess: () => {
            toast({
                title: 'Offerwall atualizado!',
                description: 'O offerwall foi atualizado com sucesso.',
            });
            queryClient.invalidateQueries({queryKey: ['rewards']});
            setIsEditDialogOpen(false);
            setSelectedReward(null);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao atualizar reward',
                description: error.message || 'Ocorreu um erro ao atualizar o offerwall.',
            });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: deleteReward,
        onSuccess: () => {
            toast({
                title: 'Offerwall deletado!',
                description: 'O reward foi deletado com sucesso.',
            });
            queryClient.invalidateQueries({queryKey: ['rewards']});
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao deletar reward',
                description: error.message || 'Ocorreu um erro ao deletar o reward.',
            });
        },
    });

    // Recebe um progresso (do POST ou do polling): guarda no card e, se terminou, avisa e para.
    const applyRetranslateProgress = useCallback((progress: RewardRetranslateProgress) => {
        setRetranslateProgress(prev => ({...prev, [progress.reward_id]: progress}));
        if (progress.status === 'running') return;

        setRetranslatingId(null);
        queryClient.invalidateQueries({queryKey: ['rewards']});

        if (progress.status === 'error') {
            toast({
                variant: 'destructive',
                title: 'Retradução falhou',
                description: progress.error || 'A API não conseguiu concluir a retradução. As traduções anteriores foram mantidas.',
            });
            return;
        }

        const failedCount = progress.failed?.length ?? 0;
        toast({
            variant: failedCount > 0 ? 'destructive' : undefined,
            title: failedCount > 0 ? 'Retradução concluída com erros' : 'Offerwall retraduzido!',
            description: failedCount > 0
                ? `${progress.done - failedCount} de ${progress.total} idiomas ok. ${failedCount} falharam e mantiveram a tradução anterior.`
                : `${progress.done} idiomas retraduzidos.`,
        });
    }, [queryClient, toast]);

    const retranslateMutation = useMutation({
        mutationFn: retranslateReward,
        onMutate: (id: number) => {
            setRetranslatingId(id);
            pollStartedAt.current = Date.now();
        },
        onSuccess: (progress) => {
            if (progress.already_running) {
                toast({
                    title: 'Retradução já em andamento',
                    description: 'Esse offerwall já está sendo retraduzido. Acompanhando o progresso sem disparar de novo.',
                });
            }
            applyRetranslateProgress(progress);
        },
        onError: (error: any) => {
            setRetranslatingId(null);
            toast({
                variant: 'destructive',
                title: 'Erro ao retraduzir',
                description: error.response?.data?.detail || error.message || 'Ocorreu um erro ao retraduzir o offerwall.',
            });
        },
    });

    // Polling enquanto há retradução rodando. Para sozinho ao terminar, em 404
    // (progresso expirou) ou ao bater o teto de tempo.
    useEffect(() => {
        if (retranslatingId === null) return;
        const id = retranslatingId;
        let cancelled = false;

        const tick = async () => {
            if (cancelled) return;
            if (Date.now() - pollStartedAt.current > RETRANSLATE_POLL_MAX_MS) {
                setRetranslatingId(null);
                toast({
                    variant: 'destructive',
                    title: 'Retradução demorou demais',
                    description: 'Parei de acompanhar. Recarregue a página para ver o estado atual.',
                });
                return;
            }
            try {
                const progress = await getRetranslateStatus(id);
                if (!cancelled) applyRetranslateProgress(progress);
            } catch (error: any) {
                if (cancelled) return;
                if (error.response?.status === 404) {
                    setRetranslatingId(null);
                    toast({
                        variant: 'destructive',
                        title: 'Progresso não encontrado',
                        description: 'A API não tem mais o andamento dessa retradução. Recarregue a página.',
                    });
                }
                // Outros erros (rede, 5xx): tenta de novo no próximo tick, até o teto.
            }
        };

        const timer = setInterval(tick, RETRANSLATE_POLL_MS);
        return () => {
            cancelled = true;
            clearInterval(timer);
        };
    }, [retranslatingId, applyRetranslateProgress, toast]);

    const handleCreate = () => {
        setIsCreateDialogOpen(true);
    };

    const handleRetranslate = (reward: Reward) => {
        setRetranslateTarget(reward);
        setRetranslateStep(1);
    };

    const closeRetranslate = () => {
        setRetranslateTarget(null);
        setRetranslateStep(1);
    };

    const confirmRetranslate = () => {
        if (!retranslateTarget) return;
        if (retranslateStep === 1) {
            setRetranslateStep(2);
            return;
        }
        const id = retranslateTarget.id;
        closeRetranslate();
        retranslateMutation.mutate(id);
    };

    const handleEdit = (reward: Reward) => {
        setSelectedReward(reward);
        setIsEditDialogOpen(true);
    };

    const handleShowCode = (reward: Reward) => {
        setSelectedReward(reward);
        setIsInstallModalOpen(true);
    };

    const handleSubmit = (formData: RewardFormData) => {
        if (isEditDialogOpen && selectedReward) {
            updateMutation.mutate({
                id: selectedReward.id,
                data: formData as Partial<RewardCreate>,
            });
        } else {
            createMutation.mutate(formData as RewardCreate);
        }
    };

    const handleDelete = (id: number) => {
        if (confirm('Tem certeza que deseja deletar este Offerwall?')) {
            deleteMutation.mutate(id);
        }
    };

    const isPending = createMutation.isPending || updateMutation.isPending;

    const filteredRewards = useMemo(() => {
        if (!rewards) return [];
        if (activeTab === 'all') return rewards;
        return rewards.filter(r => (r.type || 'standard') === activeTab);
    }, [rewards, activeTab]);

    const counts = useMemo(() => {
        if (!rewards) return {all: 0, standard: 0, quiz: 0};
        return {
            all: rewards.length,
            standard: rewards.filter(r => (r.type || 'standard') === 'standard').length,
            quiz: rewards.filter(r => r.type === 'quiz').length,
        };
    }, [rewards]);

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Offerwall</h1>
                    <p className="mt-2 text-gray-600">Gerencie seus rewards e visualize em tempo real</p>
                </div>
                <Button
                    onClick={handleCreate}
                    className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                >
                    <Plus className="mr-2 h-4 w-4"/>
                    Criar Offerwall
                </Button>
            </div>

            <InfoBanner onOpenCodeBuilder={() => setIsCodeBuilderOpen(true)}/>

            {/* Filter Tabs */}
            {rewards && rewards.length > 0 && (
                <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
                    {([
                        {key: 'all' as const, label: 'Todos', count: counts.all},
                        {key: 'standard' as const, label: 'Standard', count: counts.standard},
                        {key: 'quiz' as const, label: 'Quiz', count: counts.quiz},
                    ]).map((tab) => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                activeTab === tab.key
                                    ? 'bg-white text-gray-900 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            {tab.label}
                            <span className={`ml-1.5 text-xs ${
                                activeTab === tab.key ? 'text-gray-500' : 'text-gray-400'
                            }`}>
                                {tab.count}
                            </span>
                        </button>
                    ))}
                </div>
            )}

            {isLoading && (
                <div className="rounded-lg bg-white p-6 shadow">
                    <p className="text-center text-gray-600">Carregando rewards...</p>
                </div>
            )}

            {filteredRewards.length > 0 && (
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {filteredRewards.map((reward) => (
                        <RewardCard
                            key={reward.id}
                            reward={reward}
                            availableLanguages={availableLanguages}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            onShowCode={handleShowCode}
                            onRetranslate={handleRetranslate}
                            isDeleting={deleteMutation.isPending}
                            isRetranslating={retranslatingId === reward.id}
                            retranslateDisabled={retranslatingId !== null}
                            retranslateProgress={retranslateProgress[reward.id] ?? null}
                        />
                    ))}
                </div>
            )}

            {rewards && rewards.length === 0 && (
                <EmptyState onCreate={handleCreate}/>
            )}

            {rewards && rewards.length > 0 && filteredRewards.length === 0 && (
                <div className="rounded-lg bg-white p-8 shadow text-center">
                    <p className="text-gray-500">
                        Nenhum reward do tipo "{activeTab === 'quiz' ? 'Quiz' : 'Standard'}" encontrado
                    </p>
                </div>
            )}

            {/* Retraduzir: 1ª confirmação (o que vai acontecer) */}
            <ConfirmDialog
                open={!!retranslateTarget && retranslateStep === 1}
                onOpenChange={(v) => !v && closeRetranslate()}
                title={`Retraduzir "${retranslateTarget?.title}" nos ${retranslateTarget?.languages?.length ?? 0} idiomas selecionados?`}
                description={
                    retranslateTarget?.type === 'quiz'
                        ? 'Título, descrição, botão e todas as perguntas, opções, loader e tela final do quiz serão traduzidos de novo pelo ChatGPT, só nos idiomas que este offerwall já tem. As traduções atuais serão substituídas.'
                        : 'Título, descrição e texto do botão serão traduzidos de novo pelo ChatGPT, só nos idiomas que este offerwall já tem. As traduções atuais serão substituídas.'
                }
                confirmLabel="Continuar"
                variant="warning"
                onConfirm={confirmRetranslate}
            />

            {/* Retraduzir: 2ª confirmação (custo em tokens) */}
            <ConfirmDialog
                open={!!retranslateTarget && retranslateStep === 2}
                onOpenChange={(v) => !v && closeRetranslate()}
                title="Tem certeza? Isso consome tokens da OpenAI."
                description="Cada idioma gera uma chamada ao ChatGPT (duas no quiz), sem repetição: idioma que falhar mantém a tradução atual. Leva cerca de um minuto e você acompanha o progresso no card. Clique em OK para retraduzir agora."
                confirmLabel="OK"
                variant="danger"
                onConfirm={confirmRetranslate}
            />

            <RewardCodeBuilderModal
                isOpen={isCodeBuilderOpen}
                onClose={() => setIsCodeBuilderOpen(false)}
            />

            <RewardInstallModal
                isOpen={isInstallModalOpen}
                onClose={() => {
                    setIsInstallModalOpen(false);
                    setSelectedReward(null);
                }}
                rewardId={selectedReward?.id || 0}
            />

            <RewardFormModal
                isOpen={isCreateDialogOpen || isEditDialogOpen}
                onClose={() => {
                    setIsCreateDialogOpen(false);
                    setIsEditDialogOpen(false);
                    setSelectedReward(null);
                }}
                onSubmit={handleSubmit}
                isEditing={isEditDialogOpen}
                isPending={isPending}
                initialData={selectedReward || undefined}
                domains={domains}
                availableLanguages={availableLanguages}
            />
        </div>
    );
}